/**
 * In-app product education: multi-step tour + first-visit tips per area.
 * If you change copy or routes in a breaking way, bump `TUTORIAL_STORAGE_PREFIX`
 * in `utils/tutorialStorage.js` so existing users see the new tips once.
 */

export const PLATFORM_TOUR_STEPS = [
  {
    title: 'Welcome to your workspace',
    body:
      'Ace helps your team manage relocations end-to-end: quotations, jobs, barcodes, and tenant branding. This short tour explains where everything lives — you can replay it anytime from the Help button.',
  },
  {
    title: 'First: set up your branding',
    body:
      'Right now your quotes, invoices, and customer portal show placeholder details — "ABC Company", "XYZ Address", and a default logo. Go to Settings → Branding to add your company name, logo, colors, and address. Everything you send to customers will instantly carry your brand. The yellow banner at the top of the page takes you straight there.',
  },
  {
    title: 'Two ways to navigate',
    body:
      'Use the dark sidebar on the left for quick icons, or the top bar for the same sections as dropdown menus. Both stay in sync; expand the sidebar with the menu icon for full labels.',
  },
  {
    title: 'Operations vs document lists',
    body:
      'Operations is where work gets created: Generate Quotation, Create Barcode, New Job, Invite team, and People directory (partners, transporters, insurers, HR). Document list is where you browse what you already created: All Jobs, Quotations, Barcode library.',
  },
  {
    title: 'Generate quotation (CFT)',
    body:
      'Build customer-ready quotes with optional Excel import, inventory lines, truck suggestions, and save. Open pricing/PDF from the quotation row when you are ready for formal charges.',
  },
  {
    title: 'Jobs & barcodes',
    body:
      'New Job captures consignment and parties (you can link people from your directory). Barcodes help warehouse and field teams track cartons and assets.',
  },
  {
    title: 'Search & appearance',
    body:
      'The header search finds people, team members, and invitations (operations data), not the jobs/quotations tables — use each list’s own search there. Use the moon/sun toggle for dark mode; your choice is remembered on this device.',
  },
  {
    title: 'Settings & profile',
    body:
      'Profile is your personal account. Settings → Profile is your name, photo, and contact; Branding is the tenant look for PDFs and customer-facing flows (admins).',
  },
  {
    title: 'You are set',
    body:
      'Explore Operations first when onboarding a new customer. Need this again? Click the floating Help button at the bottom-right.',
  },
];

export const ROUTE_TIPS = [
  {
    id: 'home',
    test: (pathname) => pathname === '/',
    storageKey: 'route_home',
    title: 'Dashboard tips',
    body:
      'Charts and totals reflect jobs in your tenant. Use the sidebar or header to jump into Operations. Tenant admins can adjust seat plans and invites under Operations → Invite team.',
  },
  {
    id: 'create_job',
    test: (pathname) =>
      pathname === '/create-job' ||
      /^\/jobs\/edit\//.test(pathname) ||
      /^\/edit-job\//.test(pathname),
    storageKey: 'route_create_job',
    title: 'Creating or editing a job',
    body:
      'The form is split into steps: basics, locations, then pricing and date. Required fields must be complete before you continue. You can link directory contacts (person, insurer, company, transporter) when those roles apply.',
  },
  {
    id: 'quotation_wizard',
    test: (pathname) =>
      pathname === '/cft-calculator' ||
      pathname === '/quotations/new' ||
      /^\/quotations\/[^/]+\/edit$/.test(pathname) ||
      (/^\/quotations\/[^/]+$/.test(pathname) && pathname !== '/quotations/new'),
    storageKey: 'route_quotation_wizard',
    title: 'Generate quotation wizard',
    body:
      'Excel import is optional — you can skip straight to customer details. Only essential fields block “Next”. The last step calculates CFT, suggests trucks, and saves. Use “Copy customer intake link” after save for public collaboration.',
  },
  {
    id: 'quotations_list',
    test: (pathname) => pathname === '/quotations',
    storageKey: 'route_quotations_list',
    title: 'Quotations list',
    body:
      'Open a row to edit inventory, jump to pricing/PDF, or download PDF. Filters and search help you find customers quickly.',
  },
  {
    id: 'jobs_list',
    test: (pathname) => pathname === '/jobs',
    storageKey: 'route_jobs_list',
    title: 'All jobs',
    body:
      'Search by GCN, name, email, or city. From here you can open invoices, expenses, PDFs, and status updates for each move.',
  },
  {
    id: 'barcode_create',
    test: (pathname) => pathname === '/create-barcode',
    storageKey: 'route_barcode_create',
    title: 'Create barcode',
    body:
      'Use consistent naming so the barcode library stays searchable. Generated labels can be downloaded for print runs.',
  },
  {
    id: 'barcode_library',
    test: (pathname) => pathname === '/barcode',
    storageKey: 'route_barcode_library',
    title: 'Barcode library',
    body:
      'Browse and filter existing codes. Pair this with jobs when reconciling warehouse activity.',
  },
  {
    id: 'operations_contacts',
    test: (pathname) => pathname === '/operations/contacts',
    storageKey: 'route_ops_contacts',
    title: 'People directory',
    body:
      'Save partners, transporters, insurers, and company HR contacts once — then pick them on new jobs and quotations for consistency.',
  },
  {
    id: 'operations_team',
    test: (pathname) => pathname === '/operations/team',
    storageKey: 'route_ops_team',
    title: 'Invite team',
    body:
      'Admins choose a seat plan, send email invites, and can resend or revoke pending invites. Invited users inherit your tenant branding.',
  },
  {
    id: 'operations_search',
    test: (pathname) => pathname === '/operations/search',
    storageKey: 'route_ops_search',
    title: 'Operations search',
    body:
      'This search covers directory contacts and (for admins) team members and invites — not the jobs or quotations tables.',
  },
  {
    id: 'settings',
    test: (pathname) => pathname === '/settings-profile',
    storageKey: 'route_settings',
    title: 'Settings tabs',
    body:
      'Profile is your personal name, photo, and contact. Branding is your organization’s look for documents and customer flows (tenant admins).',
  },
  {
    id: 'organization',
    test: (pathname) => pathname === '/settings/organization',
    storageKey: 'route_org_settings',
    title: 'Organization page',
    body:
      'Full-page branding editor. For a tabbed experience with your profile, use Settings from the profile menu → Branding tab.',
  },
];
